import React, { useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./Rewards.css";

import rewardBag         from "../../Assests/reward-bag.png";
import rewardBottle      from "../../Assests/reward-bottle.png";
import rewardCertificate from "../../Assests/reward-certificate.png";
import rewardIdCard      from "../../Assests/reward-id-card.png";

/* ─────────────────────────────────────────
   3D Tilt Card — pointer-tracked rotation
   Clean image, no overlay tinting
───────────────────────────────────────── */
const TiltCard = ({ img, alt, label, gridArea }) => {
  const cardRef   = useRef(null);
  const glowRef   = useRef(null);
  const boundsRef = useRef(null);

  const onEnter = () => {
    boundsRef.current = cardRef.current.getBoundingClientRect();
  };

  const onMove = useCallback((e) => {
    if (!boundsRef.current) return;
    const b  = boundsRef.current;
    const cx = e.clientX - b.x - b.width  / 2;
    const cy = e.clientY - b.y - b.height / 2;
    const dist = Math.sqrt(cx * cx + cy * cy);
    cardRef.current.style.transform = `
      scale3d(1.04, 1.04, 1.04)
      rotate3d(${cy / 100}, ${-cx / 100}, 0, ${Math.log(dist + 1) * 2}deg)
    `;
    if (glowRef.current) {
      glowRef.current.style.backgroundImage = `
        radial-gradient(circle at
          ${cx * 2 + b.width / 2}px
          ${cy * 2 + b.height / 2}px,
          rgba(200,169,106,0.12),
          transparent 70%)
      `;
    }
  }, []);

  const onLeave = () => {
    cardRef.current.style.transform = "";
    if (glowRef.current) glowRef.current.style.backgroundImage = "";
  };

  return (
    <div
      className="reward-tilt-card"
      ref={cardRef}
      style={{ gridArea }}
      onMouseEnter={onEnter}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <img src={img} alt={alt} className="reward-tilt-img" />
      {/* Subtle glare only — does NOT darken the image */}
      <div className="reward-tilt-glow" ref={glowRef} />
      <span className="reward-tilt-label">{label}</span>
    </div>
  );
};

/* ─────────────────────────────────────────
   Letter stagger for main headline
───────────────────────────────────────── */
const headlineVariants = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.04, delayChildren: 0.3 } },
};
const letterVariants = {
  hidden:  { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};
const HEADLINE = "EARN YOUR MARK";

/* ─────────────────────────────────────────
   Main Component
───────────────────────────────────────── */
const Rewards = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section className="rewards-section" id="rewards">
      <div className="rewards-fade rewards-fade--top" />

      {/* ════════════════════════════════
          GRID LAYER — clip-path expands on click
      ════════════════════════════════ */}
      <motion.div
        className="rewards-grid-mask"
        animate={isOpen
          ? { clipPath: "inset(0% 0% round 0px)" }
          : { clipPath: "inset(50% 50% round 0px)" }
        }
        transition={{ duration: 1.15, ease: [0.55, 0.02, 0.25, 1.02] }}
        aria-hidden={!isOpen}
      >
        {/*
          5-area grid:
            tl   | center | tr
            bl   | center | br
        */}
        <div className="rewards-grid">

          {/* Top-left */}
          <TiltCard
            img={rewardBag}
            alt="Udghosh Bag"
            label="Exclusive Bag"
            gridArea="tl"
          />

          {/* Top-right */}
          <TiltCard
            img={rewardBottle}
            alt="Udghosh Bottle"
            label="Branded Bottle"
            gridArea="tr"
          />

          {/* CENTER — text panel (the image the user attached) */}
          <motion.div
            className="rewards-center-panel"
            style={{ gridArea: "center" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: isOpen ? 1 : 0 }}
            transition={{ delay: isOpen ? 0.7 : 0, duration: 0.65 }}
          >
            <div className="rewards-center-eyebrow">
              <span className="center-rule" />
              <span className="center-eyebrow-label">The Rewards</span>
              <span className="center-rule" />
            </div>
            <p className="rewards-center-headline font-display">
              {HEADLINE.split("").map((ch, i) => (
                <motion.span
                  key={i}
                  className="rewards-headline__char"
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: isOpen ? 1 : 0, y: isOpen ? 0 : 40 }}
                  transition={{ delay: isOpen ? 0.75 + i * 0.035 : 0, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                >
                  {ch === " " ? "\u00A0" : ch}
                </motion.span>
              ))}
            </p>
          </motion.div>

          {/* Bottom-left */}
          <TiltCard
            img={rewardCertificate}
            alt="CA Certificate"
            label="CA Certificate"
            gridArea="bl"
          />

          {/* Bottom-right */}
          <TiltCard
            img={rewardIdCard}
            alt="CA ID Card"
            label="VIP Entry Pass"
            gridArea="br"
          />
        </div>

        {/* Close button */}
        <AnimatePresence>
          {isOpen && (
            <motion.button
              className="rewards-close-btn"
              onClick={() => setIsOpen(false)}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ delay: 0.8, duration: 0.3 }}
              aria-label="Close rewards gallery"
            >
              ✕
            </motion.button>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ════════════════════════════════
          TEXT CONTENT LAYER (fades out on open)
      ════════════════════════════════ */}
      <motion.div
        className="rewards-inner"
        animate={{
          opacity:       isOpen ? 0 : 1,
          y:             isOpen ? -20 : 0,
          pointerEvents: isOpen ? "none" : "auto",
        }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      >
        <motion.div
          className="rewards-eyebrow-row"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <span className="rewards-eyebrow-rule" />
          <span className="rewards-eyebrow-label">The Rewards</span>
          <span className="rewards-eyebrow-rule" />
        </motion.div>

        <motion.h2
          className="rewards-headline font-display"
          variants={headlineVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          {HEADLINE.split("").map((ch, i) => (
            <motion.span key={i} className="rewards-headline__char" variants={letterVariants}>
              {ch === " " ? "\u00A0" : ch}
            </motion.span>
          ))}
        </motion.h2>

        <motion.p
          className="rewards-body udg-lede"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ delay: 0.55, duration: 0.7, ease: "easeOut" }}
        >
          No shortcuts, no handouts. Each reward here is a direct reflection
          of the effort you put in — mission by mission, campus by campus.
          The more you build, the more you gain.
        </motion.p>

        <motion.div
          className="rewards-cta-wrap"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ delay: 0.75, duration: 0.6, ease: "easeOut" }}
        >
          <button
            className="rewards-cta-link"
            onClick={() => setIsOpen(true)}
            id="rewards-view-btn"
          >
            <span>Click here to view rewards</span>
          </button>
        </motion.div>
      </motion.div>

      <div className="rewards-fade rewards-fade--bottom" />
    </section>
  );
};

export default Rewards;
